import { useState, useMemo, useEffect, useCallback } from 'react'
import { useAllServicos } from '@/hooks/useServicos'
import { formatCurrency } from '@/lib/formatters'
import {
    Shield, Clock, ShoppingBag, Star, Sparkles, Zap,
    ArrowRight, CreditCard, X, Info, CheckCircle2, Heart, Layers, ListPlus
} from 'lucide-react'
import type { Servico } from '@/types/database'
import { ContractingModal } from '@/components/ContractingModal'

export function PortalLojaPage() {
    const { data: allServicos, isLoading } = useAllServicos()

    /* Filter only services marked for online contracting and visible in portal */
    const servicos = useMemo(
        () => (allServicos || []).filter(s => s.contratacao_online && s.ativo && s.visivel_portal !== false),
        [allServicos],
    )

    /* Modal states */
    const [selectedServico, setSelectedServico] = useState<Servico | null>(null)
    const [detailServico, setDetailServico] = useState<Servico | null>(null)

    // Listen for cross-sell contracting events
    const handleCrossSellEvent = useCallback((e: Event) => {
        const svc = (e as CustomEvent).detail as Servico
        if (svc) setSelectedServico(svc)
    }, [])

    useEffect(() => {
        window.addEventListener('open-contracting', handleCrossSellEvent)
        return () => window.removeEventListener('open-contracting', handleCrossSellEvent)
    }, [handleCrossSellEvent])

    return (
        <div className="p-4 space-y-6 animate-fade-in max-w-lg mx-auto pb-10">
            {/* Header */}
            <div className="pt-2">
                <div className="flex items-center gap-2.5 mb-1">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600">
                        <ShoppingBag size={22} />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-surface-900">Loja de Serviços</h1>
                        <p className="text-sm text-surface-700/60">Sua proteção em um clique</p>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <div className="w-10 h-10 border-4 border-primary-100 border-t-primary-600 rounded-full animate-spin" />
                    <p className="text-sm text-surface-500 font-medium">Carregando ofertas...</p>
                </div>
            ) : servicos.length === 0 ? (
                <div className="text-center py-20 glass-card">
                    <div className="w-20 h-20 rounded-full bg-surface-50 flex items-center justify-center mx-auto mb-4 border border-dashed border-surface-200">
                        <ShoppingBag size={40} className="text-surface-700/10" />
                    </div>
                    <p className="text-surface-700/50 font-medium">Nenhum serviço disponível no momento.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5">
                    {servicos.map((s) => {
                        const cfg = s.config_contratacao

                        return (
                            <div key={s.id}
                                className="group relative rounded-3xl bg-white border border-surface-100 overflow-hidden shadow-sm
                                    hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                
                                {/* Popular Tag */}
                                {(s.destaque_loja || cfg?.destaque) && (
                                    <div className="absolute top-4 right-4 z-10 bg-amber-400 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1 animate-bounce-subtle">
                                        <Star size={10} fill="white" /> Popular
                                    </div>
                                )}

                                {/* Card Image or Icon Header */}
                                {s.imagem_url ? (
                                    <div className="h-40 relative">
                                        <img src={s.imagem_url} alt={s.nome} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                                    </div>
                                ) : (
                                    <div className="h-24 bg-gradient-to-br from-primary-50 to-primary-100 flex items-center px-6 relative overflow-hidden">
                                        <Shield className="absolute -right-4 -bottom-4 text-primary-200 w-32 h-32 opacity-20" />
                                        <div className="w-12 h-12 rounded-2xl bg-white/80 backdrop-blur-sm flex items-center justify-center">
                                            <Shield size={24} className="text-primary-600" />
                                        </div>
                                    </div>
                                )}

                                <div className="p-6 pt-2">
                                    <h3 className="font-bold text-surface-900 text-xl mb-1.5">{s.nome}</h3>
                                    <p className="text-sm text-surface-600 line-clamp-3 leading-relaxed mb-4">{s.descricao || 'Sem descrição disponível.'}</p>

                                    {/* Features Badges */}
                                    <div className="flex flex-wrap gap-2 mb-6">
                                        <div className="flex items-center gap-1 px-2.5 py-1.5 bg-surface-50 rounded-lg border border-surface-100 text-[10px] font-bold text-surface-600">
                                            <Clock size={12} className="text-primary-500" />
                                            {s.carencia_dias > 0 ? `Carência ${s.carencia_dias}d` : 'Início Imediato'}
                                        </div>
                                            {!!s.pontos_adesao && s.pontos_adesao > 0 && (
                                                <div className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 rounded-lg border border-rose-100 text-[10px] font-black text-rose-600 animate-pulse-subtle">
                                                    <Heart size={12} className="fill-current" />
                                                    +{s.pontos_adesao} PONTOS
                                                </div>
                                            )}
                                        {cfg?.gerar_adesao === false && (
                                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-50 rounded-lg border border-emerald-100 text-[10px] font-bold text-emerald-700">
                                                <CreditCard size={12} />
                                                ADESÃO GRÁTIS
                                            </div>
                                        )}
                                        {(s.planos?.filter(p => p.ativo)?.length || 0) > 1 && (
                                            <div className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 rounded-lg border border-indigo-100 text-[10px] font-bold text-indigo-600">
                                                <Layers size={12} />
                                                {s.planos!.filter(p => p.ativo).length} PLANOS
                                            </div>
                                        )}
                                        {(s.adicionais?.filter(a => a.ativo)?.length || 0) > 0 && (
                                            <div className="flex items-center gap-1 px-2.5 py-1.5 bg-teal-50 rounded-lg border border-teal-100 text-[10px] font-bold text-teal-600">
                                                <ListPlus size={12} />
                                                PERSONALIZE
                                            </div>
                                        )}
                                    </div>

                                    {/* Price & Action Footer */}
                                    <div className="flex items-center justify-between pt-5 border-t border-surface-100">
                                        <div>
                                            {(() => {
                                                const activePlanos = s.planos?.filter(p => p.ativo) || []
                                                const hasMultiplePlanos = activePlanos.length > 1
                                                const lowestPrice = hasMultiplePlanos
                                                    ? Math.min(...activePlanos.map(p => p.valor_mensal))
                                                    : s.valor_mensal
                                                const priceStr = formatCurrency(lowestPrice).replace('R$', '').trim()
                                                return (
                                                    <>
                                                        {hasMultiplePlanos && (
                                                            <p className="text-[9px] uppercase font-black text-indigo-500 tracking-widest mb-0.5">a partir de</p>
                                                        )}
                                                        <div className="flex items-baseline gap-1">
                                                            <span className="text-xs font-bold text-surface-400">R$</span>
                                                            <span className="text-3xl font-black text-primary-600 tracking-tighter">
                                                                {priceStr}
                                                            </span>
                                                        </div>
                                                        <p className="text-[10px] uppercase font-black text-surface-700/30 tracking-widest mt-0.5">mensalidade</p>
                                                    </>
                                                )
                                            })()}
                                        </div>
                                        
                                        <div className="flex flex-col gap-2">
                                            <button
                                                onClick={() => setSelectedServico(s)}
                                                className="btn btn-primary btn-md gap-2 shadow-primary-200/50 shadow-lg"
                                            >
                                                {s.texto_botao || 'Contratar'}
                                            </button>
                                            
                                            {s.detalhes_loja && (
                                                <button 
                                                    onClick={() => setDetailServico(s)}
                                                    className="text-[11px] font-bold text-surface-400 hover:text-primary-600 transition-colors uppercase tracking-wider text-center"
                                                >
                                                    Saiba Mais
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Contracting Modal */}
            {selectedServico && (
                <ContractingModal
                    servico={selectedServico}
                    onClose={() => setSelectedServico(null)}
                />
            )}

            {/* Learn More Modal */}
            {detailServico && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
                    <div className="bg-white rounded-[40px] w-full max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden shadow-2xl relative">
                        {/* Static Header with Close */}
                        <div className="sticky top-0 right-0 p-6 flex justify-end z-10 pointer-events-none">
                            <button 
                                onClick={() => setDetailServico(null)}
                                className="w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-surface-900 border border-surface-100 pointer-events-auto hover:bg-surface-50"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {detailServico.imagem_url && (
                             <div className="h-56 -mt-20">
                                 <img src={detailServico.imagem_url} alt={detailServico.nome} className="w-full h-full object-cover" />
                                 <div className="absolute top-0 inset-x-0 h-56 bg-gradient-to-t from-white via-transparent to-transparent" />
                             </div>
                        )}

                        <div className="p-8 pt-4">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600">
                                    <Info size={20} />
                                </div>
                                <h1 className="text-2xl font-black text-surface-900 leading-tight">
                                    Sobre o {detailServico.nome}
                                </h1>
                            </div>

                            <div className="prose prose-sm text-surface-600 leading-relaxed mb-8">
                                {detailServico.detalhes_loja?.split('\n').map((line, i) => (
                                    <p key={i} className="mb-2 flex items-start gap-2">
                                        {line.trim().startsWith('-') ? (
                                            <span className="mt-1"><CheckCircle2 size={16} className="text-emerald-500" /></span>
                                        ) : null}
                                        {line.trim().startsWith('-') ? line.replace('-', '').trim() : line}
                                    </p>
                                ))}
                            </div>

                            <button
                                onClick={() => {
                                    setSelectedServico(detailServico)
                                    setDetailServico(null)
                                }}
                                className="w-full btn btn-primary py-4 text-lg rounded-2xl gap-3"
                            >
                                {detailServico.texto_botao || 'Contratar Agora'} <ArrowRight size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
